import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { Phone, MessageCircle, Mail, Bell, Plus } from 'lucide-react';
import { handleApiError } from '../utils/errorHandler';
import Modal from './Modal';
import ActivityForm from './ActivityForm';
import ActivityTimeline from './ActivityTimeline';
import CreateReminderModal from './dashboard/CreateReminderModal';
import NotesSection from './NotesSection';
import { useAuth } from '../contexts/AuthContext';
import { 
  LeadDto, 
  LeadActivityDto,
  leadsApi,
  formatDate 
} from '../services/leadsService';
import {
  EntityType,
  CreateActivityDto,
  ActivityStatisticsDto,
  activityApi,
  createActivityWithReminder
} from '../services/activityService';
import { hexToRgba } from '../utils/colorUtils';

interface LeadHandlingModalProps {
  isOpen: boolean;
  onClose: () => void;
  lead: LeadDto;
  onLeadUpdated?: () => void;
}

const TabContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: 2px solid var(--border-color);
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 12px 20px;
  background: none;
  border: none;
  font-size: 14px;
  font-weight: 500;
  color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-secondary)'};
  cursor: pointer;
  position: relative;
  transition: color 0.2s ease;

  &:hover {
    color: var(--primary-color);
  }

  ${props => props.$active && `
    &::after {
      content: '';
      position: absolute;
      bottom: -2px;
      left: 0;
      right: 0;
      height: 2px;
      background: var(--primary-color);
    }
  `}
`;

const TabContent = styled.div`
  min-height: 400px;
`;

const LeadInfoSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const InfoCard = styled.div`
  background: var(--surface-light);
  padding: 16px;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
`;

const InfoTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  margin: 0 0 12px 0;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: var(--text-secondary);
  min-width: 80px;
`;

const InfoValue = styled.span`
  font-size: 14px;
  color: var(--text-primary);
  font-weight: 500;
`;

const PhoneLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  
  &:hover {
    text-decoration: underline;
  }
`;

const StatusBadge = styled.span<{ color: string; bgColor: string; borderColor: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  background: ${props => props.bgColor};
  color: ${props => props.color};
  border: 1px solid ${props => props.borderColor};
  filter: saturate(0.8) brightness(0.95);
`;

const QuickActions = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const QuickActionButton = styled.button`
  padding: 8px 16px;
  background: var(--surface-color);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover {
    background: var(--hover-background);
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

const PhoneIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-right: 4px;
  color: var(--primary-color);
`;

const StatisticsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: var(--surface-light);
  padding: 16px;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: var(--text-secondary);
`;

const GeneralNotesSection = styled.div`
  background: var(--surface-light);
  padding: 16px;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--border-color);
  margin-top: 16px;
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 10px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  margin-top: 8px;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const NotesActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`;

const SaveButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 8px 16px;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  ${props => props.variant === 'primary' ? `
    background: var(--primary-color);
    color: white;
    border: 1px solid var(--primary-color);
    &:hover:not(:disabled) { background: transparent; color: var(--primary-color); }
  ` : `
    background: var(--surface-color);
    color: var(--text-primary);
    border: 1px solid var(--border-color);
    &:hover:not(:disabled) { background: var(--hover-background); }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

type TabType = 'info' | 'activity' | 'history';

const LeadHandlingModal: React.FC<LeadHandlingModalProps> = ({
  isOpen,
  onClose,
  lead,
  onLeadUpdated
}) => {
  const { user, isManager } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [showActivityForm, setShowActivityForm] = useState(false);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [statistics, setStatistics] = useState<ActivityStatisticsDto | null>(null);
  const [notes, setNotes] = useState(lead.notes || '');
  const [savingNotes, setSavingNotes] = useState(false);
  const [activities, setActivities] = useState<LeadActivityDto[]>([]);

  const loadStatistics = useCallback(async () => {
    try {
      const stats = await activityApi.getStatistics(EntityType.Lead, lead.id);
      setStatistics(stats);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטעינת הסטטיסטיקות'));
    }
  }, [lead.id]);

  const loadActivities = useCallback(async () => {
    try {
      const activitiesData = await leadsApi.getActivities(lead.id);
      setActivities(activitiesData);
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בטעינת הפעילויות'));
    }
  }, [lead.id]);

  const handleNotesChange = (updatedActivities: LeadActivityDto[]) => {
    setActivities(updatedActivities);
    if (onLeadUpdated) onLeadUpdated();
  };

  useEffect(() => {
    if (isOpen && lead) {
      loadStatistics();
      loadActivities();
      setNotes(lead.notes || '');
    }
  }, [isOpen, lead, loadStatistics, loadActivities]);

  const handleActivitySubmit = async (activity: CreateActivityDto, reminderDateTime: string | null) => {
    try {
      await createActivityWithReminder(
        activity,
        reminderDateTime
          ? { dueDateTime: reminderDateTime, entityName: lead.fullName, userId: user?.id || 0 }
          : null
      );

      setShowActivityForm(false);
      loadStatistics();
      if (onLeadUpdated) onLeadUpdated();
    } catch (error) {
      console.error('Error creating activity:', error);
      throw error;
    }
  };

  const handleNotesUpdate = async () => {
    try {
      setSavingNotes(true);
      await leadsApi.partialUpdate(lead.id, { notes });
      if (onLeadUpdated) onLeadUpdated();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בעדכון ההערות'));
    } finally {
      setSavingNotes(false);
    }
  };

  const handleQuickCall = () => {
    // Create phone activity
    setShowActivityForm(true);
  };

  const handleQuickWhatsApp = () => {
    const primaryPhone = lead.contacts?.find(c => c.isPrimary)?.number
      ?? lead.contacts?.[0]?.number
      ?? '';
    if (primaryPhone) {
      window.open(`https://wa.me/972${primaryPhone.replace(/^0/, '')}`, '_blank');
      setShowActivityForm(true);
    }
  };

  const handleQuickEmail = () => {
    if (lead.email) {
      window.location.href = `mailto:${lead.email}`;
      setShowActivityForm(true);
    }
  };

  const formatPhoneForDisplay = (phone: string) => {
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={`טיפול בליד - ${lead.fullName}`}
        width="800px"
        maxWidth="95vw"
      >
        <QuickActions>
          <QuickActionButton onClick={handleQuickCall}>
            <Phone size={16} strokeWidth={2} /> התקשר
          </QuickActionButton>
          <QuickActionButton onClick={handleQuickWhatsApp}>
            <MessageCircle size={16} strokeWidth={2} /> וואטסאפ
          </QuickActionButton>
          {lead.email && (
            <QuickActionButton onClick={handleQuickEmail}>
              <Mail size={16} strokeWidth={2} /> שלח אימייל
            </QuickActionButton>
          )}
          <QuickActionButton onClick={() => setShowReminderModal(true)}>
            <Bell size={16} strokeWidth={2} /> צור תזכורת
          </QuickActionButton>
          <QuickActionButton onClick={() => setShowActivityForm(true)}>
            <Plus size={16} strokeWidth={2} /> תעד פעילות
          </QuickActionButton>
        </QuickActions>

        <TabContainer>
          <Tab $active={activeTab === 'info'} onClick={() => setActiveTab('info')}>
            פרטי ליד
          </Tab>
          <Tab $active={activeTab === 'activity'} onClick={() => setActiveTab('activity')}>
            תיעוד פעילות
          </Tab>
          <Tab $active={activeTab === 'history'} onClick={() => setActiveTab('history')}>
            היסטוריה
          </Tab>
        </TabContainer>

        <TabContent>
          {activeTab === 'info' && (
            <>
              <LeadInfoSection>
                <InfoCard>
                  <InfoTitle>פרטי התקשרות</InfoTitle>
                  <InfoItem>
                    <InfoLabel>שם מלא:</InfoLabel>
                    <InfoValue>{lead.fullName}</InfoValue>
                  </InfoItem>
                  {(lead.contacts ?? [])
                    .slice()
                    .sort((a, b) => {
                      if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1;
                      return a.sortOrder - b.sortOrder;
                    })
                    .map((c, idx) => (
                      <InfoItem key={c.id || `new-${idx}`}>
                        <InfoLabel>{c.isPrimary ? 'איש קשר ראשי:' : (c.label ? `${c.label}:` : 'איש קשר:')}</InfoLabel>
                        <InfoValue>
                          <PhoneLink href={`tel:${c.number}`}>
                            {formatPhoneForDisplay(c.number)} <PhoneIconWrapper><Phone size={14} strokeWidth={2} /></PhoneIconWrapper>
                          </PhoneLink>
                          {c.email && (
                            <span style={{ marginRight: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <Mail size={14} strokeWidth={2} />
                              <a href={`mailto:${c.email}`} dir="ltr">{c.email}</a>
                            </span>
                          )}
                        </InfoValue>
                      </InfoItem>
                    ))}
                  {lead.email && (
                    <InfoItem>
                      <InfoLabel>דוא"ל ראשי:</InfoLabel>
                      <InfoValue>{lead.email}</InfoValue>
                    </InfoItem>
                  )}
                </InfoCard>

                <InfoCard>
                  <InfoTitle>מידע כללי</InfoTitle>
                  <InfoItem>
                    <InfoLabel>סטטוס:</InfoLabel>
                    <InfoValue>
                      <StatusBadge
                        color={lead.statusColor}
                        bgColor={hexToRgba(lead.statusColor, 0.1)}
                        borderColor={hexToRgba(lead.statusColor, 0.25)}
                      >
                        {lead.statusName}
                      </StatusBadge>
                    </InfoValue>
                  </InfoItem>
                  <InfoItem>
                    <InfoLabel>נוצר ב:</InfoLabel>
                    <InfoValue>{formatDate(lead.createdAt)}</InfoValue>
                  </InfoItem>
                  {lead.lastUpdatedAt && (
                    <InfoItem>
                      <InfoLabel>עודכן:</InfoLabel>
                      <InfoValue>{formatDate(lead.lastUpdatedAt)}</InfoValue>
                    </InfoItem>
                  )}
                </InfoCard>
              </LeadInfoSection>

              {statistics && (
                <StatisticsSection>
                  <StatCard>
                    <StatValue>{statistics.totalActivities}</StatValue>
                    <StatLabel>סה"כ פעילויות</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{statistics.phoneCalls}</StatValue>
                    <StatLabel>שיחות טלפון</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{statistics.whatsAppMessages}</StatValue>
                    <StatLabel>הודעות וואטסאפ</StatLabel>
                  </StatCard>
                  <StatCard>
                    <StatValue>{statistics.meetings}</StatValue>
                    <StatLabel>פגישות</StatLabel>
                  </StatCard>
                </StatisticsSection>
              )}

              <GeneralNotesSection>
                <InfoTitle>הערות כלליות</InfoTitle>
                <NotesTextarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="הוסף הערות כלליות על הליד..."
                  disabled={savingNotes}
                />
                <NotesActions>
                  <SaveButton 
                    variant="primary" 
                    onClick={handleNotesUpdate}
                    disabled={savingNotes || notes === (lead.notes || '')}
                  >
                    {savingNotes ? 'שומר...' : 'שמור הערות'}
                  </SaveButton>
                </NotesActions>
              </GeneralNotesSection>

              {/* Notes CRUD Section */}
              <NotesSection
                leadId={lead.id}
                activities={activities}
                onNotesChange={handleNotesChange}
                currentUserId={user?.id}
                isSystemAdmin={isManager}
              />
            </>
          )}

          {activeTab === 'activity' && (
            <>
              {showActivityForm ? (
                <ActivityForm
                  entityType={EntityType.Lead}
                  entityId={lead.id}
                  onSubmit={handleActivitySubmit}
                  onCancel={() => setShowActivityForm(false)}
                />
              ) : (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <p style={{ marginBottom: '20px', color: 'var(--text-secondary)' }}>
                    בחר פעולה מהכפתורים המהירים למעלה או לחץ על הכפתור למטה
                  </p>
                  <QuickActionButton onClick={() => setShowActivityForm(true)}>
                    <Plus size={16} strokeWidth={2} /> תעד פעילות חדשה
                  </QuickActionButton>
                </div>
              )}
            </>
          )}

          {activeTab === 'history' && (
            <ActivityTimeline
              entityType={EntityType.Lead}
              entityId={lead.id}
              showAddButton={true}
              onActivityAdded={() => {
                setActiveTab('activity');
                setShowActivityForm(true);
              }}
            />
          )}
        </TabContent>
      </Modal>

      <CreateReminderModal
        isOpen={showReminderModal}
        onClose={() => setShowReminderModal(false)}
        onSave={() => {
          setShowReminderModal(false);
          if (onLeadUpdated) onLeadUpdated();
        }}
        prefilledEntityType={EntityType.Lead}
        prefilledEntityId={lead.id}
      />
    </>
  );
};

export default LeadHandlingModal;